// Charts plot one bar per year of data. When a stretch of years has no data (a "data void"), showing
// every empty year wastes horizontal space. This helper remaps the real (data-bearing) years onto
// sequential positions so data columns sit adjacent, and collapses each run of missing years into a
// single column labelled with the missing range (e.g. "2011–2015", or just "2013" for a single
// missing year). Multi-year voids are given double the width of a normal column so the range label
// fits and the collapse reads clearly. When `bounds` are supplied, leading/trailing runs of empty
// years (before the first / after the last data year) are collapsed the same way.

const EN_DASH = '\u2013';

// Width (in position units) of a collapsed void column. Single missing years keep a normal
// one-column width; multi-year voids are widened.
const SINGLE_VOID_WIDTH = 1;
const MULTI_VOID_WIDTH = 2;

export interface CollapsedTick {
  // Center position on the collapsed axis.
  pos: number;
  // Present for real data columns; absent for the collapsed void columns.
  year?: number;
  label: string;
  // Left/right edges (position units) of a void column; only set for voids.
  start?: number;
  end?: number;
  // True when a void collapses more than one missing year (something was actually compressed).
  multiYear?: boolean;
}

export interface CollapsedGapLayout {
  // Real year -> its center position on the collapsed axis.
  posByYear: Map<number, number>;
  // All axis ticks (real years and collapsed voids) in left-to-right order.
  ticks: CollapsedTick[];
  // Total width of the axis in position units, including widened void columns.
  columnCount: number;
  // Center position of the first / last real data column (used for the x-domain).
  firstPos: number;
  lastPos: number;
  // Whether a collapsed void occupies the very start / end of the axis.
  hasLeadingVoid: boolean;
  hasTrailingVoid: boolean;
}

interface CollapseBounds {
  minYear: number;
  maxYear: number;
}

export const collapseDataGaps = (years: number[], bounds?: CollapseBounds): CollapsedGapLayout => {
  const sorted = Array.from(new Set(years)).sort((a, b) => a - b);
  const posByYear = new Map<number, number>();
  const ticks: CollapsedTick[] = [];
  let cursor = 0;
  let firstPos = 0.5;
  let lastPos = 0.5;
  let hasLeadingVoid = false;
  let hasTrailingVoid = false;

  const pushVoid = (fromYear: number, toYear: number) => {
    const multi = toYear > fromYear;
    const width = multi ? MULTI_VOID_WIDTH : SINGLE_VOID_WIDTH;
    const start = cursor;
    const end = cursor + width;
    const label = multi ? `${fromYear}${EN_DASH}${toYear}` : String(fromYear);
    ticks.push({ pos: start + width / 2, label, start, end, multiYear: multi });
    cursor += width;
  };

  if (sorted.length === 0) {
    return { posByYear, ticks, columnCount: cursor, firstPos, lastPos, hasLeadingVoid, hasTrailingVoid };
  }

  // Leading void: empty years before the first data year.
  if (bounds && bounds.minYear < sorted[0]) {
    pushVoid(bounds.minYear, sorted[0] - 1);
    hasLeadingVoid = true;
  }

  for (let i = 0; i < sorted.length; i++) {
    const year = sorted[i];
    const center = cursor + 0.5;
    posByYear.set(year, center);
    ticks.push({ pos: center, year, label: String(year) });
    if (i === 0) {
      firstPos = center;
    }
    lastPos = center;
    cursor += 1;

    if (i < sorted.length - 1) {
      const gapStart = year + 1;
      const gapEnd = sorted[i + 1] - 1;
      if (gapEnd >= gapStart) {
        pushVoid(gapStart, gapEnd);
      }
    }
  }

  // Trailing void: empty years after the last data year.
  if (bounds && bounds.maxYear > sorted[sorted.length - 1]) {
    pushVoid(sorted[sorted.length - 1] + 1, bounds.maxYear);
    hasTrailingVoid = true;
  }

  return { posByYear, ticks, columnCount: cursor, firstPos, lastPos, hasLeadingVoid, hasTrailingVoid };
};


